export const STORAGE_KEYS = {
  token: 'msx_token',
  user: 'msx_user',
  apiBase: 'msx_api_base',
}

export const DEFAULT_API_BASE = process.env.VUE_APP_API_BASE || ''

export const STATUS_MAP = {
  pending: { label: '历史待物业科处理', type: 'warning' },
  property_assigned: { label: '物业维修待接单', type: 'primary' },
  property_processing: { label: '物业处理中', type: 'primary' },
  property_returned: { label: '退回物业科待处理', type: 'danger' },
  company_pending: { label: '待维修公司接单', type: 'warning' },
  maintenance_pending: { label: '待维修科分派', type: 'warning' },
  company_assigned: { label: '已派维修公司', type: 'primary' },
  company_surveying: { label: '维修公司勘查中', type: 'primary' },
  maintenance_review: { label: '待维修科复核报价', type: 'warning' },
  deputy_review: { label: '待副职签字', type: 'warning' },
  leader_review: { label: '待正职签字', type: 'warning' },
  cost_review: { label: '待造价科审核', type: 'warning' },
  quotation_rejected: { label: '报价资料已退回', type: 'danger' },
  maintenance_confirm: { label: '待维修科确认执行', type: 'warning' },
  execution: { label: '施工执行中', type: 'primary' },
  completion_review: { label: '待维修科验收', type: 'warning' },
  done: { label: '已完成', type: 'success' },
  assigned: { label: '历史已派单', type: 'info' },
  processing: { label: '历史处理中', type: 'info' },
  submitted: { label: '历史预算审核中', type: 'info' },
  approved: { label: '历史审核通过', type: 'info' },
  rejected: { label: '历史审核不通过', type: 'info' },
}

// 业务流转路线：前半段分「派师傅」「派公司」两条，退回后才进维修科流程。
export const ROUTE_MAP = {
  property: '物业直修（派师傅）',
  company: '公司直修（直接派公司）',
  maintenance: '维修科流程（退回后）',
}

export const ROLE_MAP = {
  admin: '物业科人员',
  worker: '物业维修人员',
  maintenance: '维修科人员',
  company: '维修公司人员',
  deputy: '副职',
  leader: '正职',
  reviewer: '造价科',
  system_admin: '系统管理员',
  student: '学生/教师',
}

// 「账号管理」页可创建/改派的员工角色。与后端 `trunk/models.py` 的 `MANAGEABLE_ROLES` 一致：
// 不含 student（学生账号由微信授权/注册产生），不含 unassigned（微信授权后未分配身份的中间态）。
// system_admin 只有系统管理员能创建或改派（后端强制，前端对物业科隐藏该选项）。
export const MANAGEABLE_ROLES = ['worker', 'admin', 'maintenance', 'company', 'deputy', 'leader', 'reviewer', 'system_admin']

export const ACCOUNT_ROLE_OPTIONS = MANAGEABLE_ROLES.map((value) => ({ value, label: ROLE_MAP[value] }))

export const ROLE_META = {
  system_admin: {
    portal: '系统管理员端', title: '系统管理工作台', description: '管理微信 OpenID 账号与业务身份授权', mark: '管',
    theme: '#1677d2', todayLabel: '今日新增', todayField: 'created_at',
    statuses: ['property_assigned', 'property_processing', 'property_returned', 'company_pending', 'maintenance_pending', 'company_assigned', 'company_surveying', 'maintenance_review', 'deputy_review', 'leader_review', 'cost_review', 'quotation_rejected', 'maintenance_confirm', 'execution', 'completion_review', 'done'],
    defaultStatus: '',
  },
  admin: {
    portal: '物业科端', title: '物业科工作台', description: '处理师傅/公司退回的工单：预估金额后转维修科', mark: '物',
    theme: '#4f7df3',
    todayLabel: '今日已派',
    todayField: 'assign_time',
    statuses: ['property_returned', 'done'],
    hiddenStatusCards: ['deputy_review', 'completion_review'],
    defaultStatus: 'property_returned',
  },
  reviewer: {
    portal: '造价科端', title: '造价科工作台', description: '两级签字后的造价合理性独立审核',
    mark: '价',
    theme: '#0891b2',
    todayLabel: '今日已审',
    todayField: 'review_time',
    statuses: ['cost_review', 'quotation_rejected', 'maintenance_confirm', 'execution', 'completion_review', 'done'],
    // 维修科确认维修公司异议后退回重审的工单，单独一张卡片与首次待审核区分。
    subStatusCards: [
      { key: 'cost_review_objection', parent: 'cost_review', countKey: 'cost_review_objection', label: '重审退回' },
    ],
    defaultStatus: 'cost_review',
  },
  worker: {
    portal: '物业维修端', title: '物业维修工作台', description: '接单上门维修；做不了就退回物业科', mark: '修',
    theme: '#f26b38',
    todayLabel: '今日完工',
    todayField: 'done_time',
    // company_pending / company_assigned / execution / company_surveying：挂了维修公司的师傅
    // 还会收到「本公司被直派」的工单（空调/热水/洗衣机/直饮水/电梯/其他）；其中**报价单**
    // （workflow_mode=quotation）公司接单后会进入 `company_surveying`（去现场勘查报价）。
    // ⚠️ 2026-09-17 补两个，都是**卡片漏状态 ⇒ 工单从「共X项」里静默消失**这一类（本项目铁律）：
    //   ① `company_surveying`：公司接报价单后去勘查报价的单；
    //   ② `company_assigned`：**维修科把退回单分派给本公司**、还没人接单的单
    //      （用户实测：分派给空调维修公司后，该公司的李维修看不到也收不到）。
    // ⚠️ 2026-10-08 再补 `quotation_rejected`（报价资料已退回）：用户报「维修工卡片里『已退回』是 0，
    //    但列表里明明有一条报价被退回的单，而且 8 条只数出 7 条」——该状态当时在小程序卡片和这里
    //    **都没有**，整条单从「共X项」里静默消失。两处必须成对加（小程序 `pages/worker/tasks.vue`）。
    statuses: ['property_assigned', 'company_assigned', 'company_pending', 'property_processing', 'company_surveying', 'quotation_rejected', 'execution', 'completion_review', 'property_returned', 'done'],
    defaultStatus: 'property_assigned',
    // ⚠️ 卡片文案按**小程序端 `pages/worker/tasks.vue` 的口径**（用户 2026-09-18：
    //    「李维修卡片的状态种类可以参照小程序端」＋「是不是要区分维修科分派的和公司直派的」）。
    //    同一个状态在**物业科**眼里叫「已派维修公司」，在**公司旗下维修工**眼里必须说清是哪种派单：
    //      · `company_assigned` = **维修科分派**（被退回的单转过来）→「维修科派单待接」
    //      · `company_pending`  = **类型→公司直接派**（公司直派，不经过物业科）→「公司直派待接」
    //    这两个原来分别显示成「已派维修公司」「待维修公司接单」，看不出区别、也不是本端视角。
    //    另外 `property_assigned` 在本端是「待接单」（不是物业科的「物业维修待接单」）。
    statusLabels: {
      property_assigned: '待接单',
      company_assigned: '维修科派单待接',
      company_pending: '公司直派待接',
      property_processing: '待施工',
      execution: '公司施工中',
      company_surveying: '公司勘查中',
      completion_review: '待维修科验收',
      property_returned: '已退回',
      done: '已完成',
    },
  },
  maintenance: { portal: '维修科端', title: '维修科工作台', description: '按预估金额分派公司，并完成报价复核或直接维修验收', mark: '科', theme: '#2563eb', todayLabel: '今日结单', todayField: 'done_time', statuses: ['maintenance_pending', 'company_assigned', 'company_surveying', 'maintenance_review', 'deputy_review', 'leader_review', 'cost_review', 'maintenance_confirm', 'execution', 'completion_review', 'done'], dashboardHiddenStatusCards: ['company_surveying', 'deputy_review', 'cost_review'], defaultStatus: 'maintenance_pending',
    // 维修公司已提异议、维修科尚未定夺的工单，单独一张卡片。
    subStatusCards: [
      { key: 'company_objection', parent: 'maintenance_review', countKey: 'company_objection', label: '公司异议' },
    ] },
  company: { portal: '维修公司端', title: '维修公司工作台', description: '接公司直派的单直接施工；做不了就退回物业科；报价单完成勘查与报价', mark: '企', theme: '#0f766e', todayLabel: '今日完工申请', todayField: 'completion_submit_time', statuses: ['company_pending', 'company_assigned', 'company_surveying', 'maintenance_review', 'quotation_rejected', 'maintenance_confirm', 'execution', 'completion_review', 'done'], dashboardHiddenStatusCards: ['company_assigned'], defaultStatus: 'company_pending',
    // 已提交对造价科修改版异议的工单，单独一张卡片，与“报价审核中”互斥。
    subStatusCards: [
      { key: 'company_objection', parent: 'maintenance_review', countKey: 'company_objection', label: '已提异议' },
      // 团队处理中（2026-09-17 用户口径）：本公司名下师傅（派工区域派出去）正在干的物业直修单。
      // 计数键 `company_team_processing` 由 `/api/stats/` 返回；点击后用 `?team_processing=true` 精确筛。
      { key: 'team_processing', parent: 'company_pending', countKey: 'company_team_processing', label: '团队处理中' },
    ] },
  deputy: { portal: '副职审批端', title: '副职审批工作台', description: '核对造价资料并完成第一顺位签字', mark: '副', theme: '#9333ea', todayLabel: '今日已签', todayField: 'deputy_approved_time', statuses: ['deputy_review', 'leader_review'], defaultStatus: 'deputy_review' },
  leader: { portal: '正职审批端', title: '正职审批工作台', description: '副职签字后完成正职签字', mark: '正', theme: '#5366d7', todayLabel: '今日已签', todayField: 'leader_approved_time', statuses: ['leader_review', 'cost_review'], defaultStatus: 'leader_review' },
  student: {
    portal: '用户端',
    title: '报修工作台',
    description: '查看个人报修进度',
    mark: '学',
    theme: '#1768ac',
    todayLabel: '今日提交',
    todayField: 'created_at',
    statuses: ['property_assigned', 'property_processing', 'property_returned', 'maintenance_pending', 'company_assigned', 'company_surveying', 'maintenance_review', 'deputy_review', 'leader_review', 'cost_review', 'maintenance_confirm', 'execution', 'completion_review', 'done'],
    defaultStatus: '',
  },
}

export const REPAIR_TYPES = ['水电卫浴', '空调', '热水', '洗衣机', '直饮水', '家具', '电梯', '其他']
export const REPAIR_UNITS = [
  '水电维修组',
  '空调维修组',
  '热水/直饮水维修组',
  '洗衣机维修组',
  '家具维修组',
  '电梯维修组',
  '综合维修组',
  '后勤维修中心',
  '外协维修单位',
]

export const MAINTENANCE_COMPANIES = ['水电维修公司', '空调维修公司', '土木维修公司', '基建维修公司', '电梯维修公司', '综合维修公司']

// 前端**合成状态**（不是后端返回的 order.status，而是工作台 / 工单管理页自己拼出来的卡片筛选值）。
// 这些 key 不在 STATUS_MAP 里，`statusLabel()` 必须显式给出中文，否则卡片会直接显示英文 key
// ——工作台最后那张总计卡片会显示成「all」，副职/正职工作台会显示成「signed」。
export const SYNTHETIC_STATUS_LABELS = {
  all: '全部',
  signed: '已签字',
  cost_review_objection: '重审退回',
  company_objection: '公司异议',
  // 维修公司账号看「本公司名下师傅正在处理的物业直修单」（2026-09-17 用户口径）
  team_processing: '团队处理中',
}

/** 状态中文名。**第二个参数传角色**时会先用该角色的卡片文案（`ROLE_META[role].statusLabels`）——
 *  同一个状态在不同端叫法不同（例：`company_assigned` 物业科看是「已派维修公司」，
 *  公司旗下维修工看是「维修科派单待接」；与小程序端 `pages/worker/tasks.vue` 对齐）。
 */
export function statusLabel(status, role) {
  const roleLabels = (ROLE_META[role] || {}).statusLabels || {}
  if (roleLabels[status]) return roleLabels[status]
  if (SYNTHETIC_STATUS_LABELS[status]) return SYNTHETIC_STATUS_LABELS[status]
  return (STATUS_MAP[status] || {}).label || status || '-'
}

//: 只有「挂了维修公司的师傅」才会遇到的四个状态（公司侧的活）。独立师傅（没有 `company_name`）
//: 后端可见范围里根本不会给他这些单，工作台上却一直挂着「公司直派待接 / 公司施工中 / 公司勘查中」
//: 这些恒为 0 的卡片 —— 用户 2026-09-18：「芮维修是物业维修人员（独立），那么她不应该有公司直派、待勘察」。
export const COMPANY_ONLY_WORKER_STATUSES = ['company_assigned', 'company_pending', 'execution', 'company_surveying']

/** 本端工作台/列表要显示的状态卡片：`worker` 且没挂公司时，去掉公司侧那四个状态。
 *
 *  ⚠️ 与小程序端 `pages/worker/tasks.vue` 的 `buildStatuses()` 同一口径，改一处要改两处：
 *    · 独立师傅（没有 `company_name`）→ 去掉**公司侧四态**；
 *    · 只挂**公司工种**（空调等）的师傅 → 再去掉「待接单」（公共待接单池不对他开放，
 *      用户 2026-09-18：「公共池只对『物业直修工种 / 独立师傅』开放」）。
 *      `can_take_property_pool` 由后端 `/api/auth/profile/` 给（`models.worker_can_take_property_pool()`）；
 *      老缓存里没有这个字段时按「能接」处理，不凭猜测藏卡片。
 *  ⚠️ 万一还是有一条单落在这些状态上（例如物业科**手工**派给他），Web 的 `statusCards`
 *     会自动补一张卡（`extras`：有数量但不在配置里的状态），不会静默消失。
 */
export function visibleStatusesForRole(role, user) {
  const meta = ROLE_META[role] || ROLE_META.student
  const statuses = (meta.statuses || []).slice()
  if (role !== 'worker') return statuses
  const account = user || {}
  const companyName = String(account.company_name || account.companyName || '').trim()
  if (!companyName) return statuses.filter((status) => !COMPANY_ONLY_WORKER_STATUSES.includes(status))
  if (account.can_take_property_pool === false) {
    return statuses.filter((status) => status !== 'property_assigned')
  }
  return statuses
}
